import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { uploadFile } from '../lib/uploader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { UploadCloud, ShieldCheck, Zap, Loader2, CheckCircle2, PauseCircle, AlertTriangle, ArrowRight } from 'lucide-react';

const DEFAULT_ACCENT = '#FF6B35';

export default function Portal() {
    const { slug } = useParams();
    const [portal, setPortal] = useState(null);
    const [error, setError] = useState(null);
    const [clientName, setClientName] = useState('');
    const [clientNotes, setClientNotes] = useState('');
    const [files, setFiles] = useState([]);
    const [dragOver, setDragOver] = useState(false);
    const [limitModal, setLimitModal] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => {
        api.get(`/portals/public/${slug}`).then((r) => setPortal(r.data)).catch(() => setError('Portal not found'));
    }, [slug]);

    // Branding is already tier-filtered server-side (see public_branding_for).
    const branding = portal?.branding || {};
    const accent = useMemo(() => branding.brand_accent_hex || DEFAULT_ACCENT, [branding.brand_accent_hex]);
    const hasCustomLogo = !!branding.brand_logo_url;
    const hasCustomBrandName = !!branding.brand_name_string;
    const welcomeMessage = branding.brand_welcome_message || null;

    const startFiles = useCallback(async (fileList) => {
        if (!clientName.trim()) {
            toast.error('Please enter your name first');
            return;
        }
        const arr = Array.from(fileList);
        for (const f of arr) {
            const localId = `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 6)}`;
            const ctrl = uploadFile({
                file: f,
                portalSlug: slug,
                clientName,
                clientNotes,
                onProgress: ({ pct, completed, total }) => {
                    setFiles((prev) => prev.map((x) => x.id === localId ? { ...x, pct, completed, total } : x));
                },
                onStatus: (status) => {
                    setFiles((prev) => prev.map((x) => x.id === localId ? { ...x, status } : x));
                },
            });
            setFiles((prev) => [...prev, { id: localId, name: f.name, size: f.size, pct: 0, status: 'queued', ctrl }]);
            try {
                await ctrl.promise;
                setFiles((prev) => prev.map((x) => x.id === localId ? { ...x, status: 'completed', pct: 100 } : x));
                toast.success(`${f.name} uploaded`);
            } catch (e) {
                setFiles((prev) => prev.map((x) => x.id === localId ? { ...x, status: 'failed' } : x));
                if (e?.code === 'PIPELINE_LIMIT_REACHED') {
                    setLimitModal(e.details || { message: e.message });
                    return;
                }
                toast.error(`${f.name} failed: ${e.message || 'upload error'}`);
            }
        }
    }, [clientName, clientNotes, slug]);

    if (error) return <ErrorScreen message={error} />;
    if (!portal) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading portal…</div>;

    return (
        <div
            className="min-h-screen flex flex-col bg-background text-foreground"
            style={{ '--portal-accent': accent }}
            data-testid="portal-root"
        >
            {/* Portal header — logo/brand-name swap for Ultimate tier owners */}
            <header className="border-b border-border">
                <div className="mx-auto max-w-4xl px-6 py-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {hasCustomLogo ? (
                            <img
                                data-testid="portal-brand-logo"
                                src={branding.brand_logo_url}
                                alt={branding.brand_name_string || 'Studio logo'}
                                className="h-9 max-w-[10rem] object-contain"
                            />
                        ) : (
                            <div
                                className="h-8 w-8 grid place-items-center text-white"
                                style={{ backgroundColor: accent }}
                            >
                                <Zap className="h-4 w-4" strokeWidth={2.5} />
                            </div>
                        )}
                        <div>
                            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                {hasCustomBrandName ? branding.brand_name_string : 'Magnidrop'} portal
                            </p>
                            <p data-testid="portal-title" className="font-heading text-lg">{portal.portal.title}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        {portal.owner.picture && <img src={portal.owner.picture} alt="" className="h-8 w-8 rounded-full border border-border" />}
                        <div className="hidden sm:block">
                            <p className="text-xs text-muted-foreground">Sending to</p>
                            <p className="text-foreground">{portal.owner.name}</p>
                        </div>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-4xl px-6 py-16 flex-1 w-full">
                {/* Custom welcome greeting — Pro & Ultimate only */}
                {welcomeMessage && (
                    <div className="mb-10" data-testid="portal-welcome-message">
                        <p
                            className="text-xs uppercase tracking-[0.2em] mb-3"
                            style={{ color: accent }}
                        >
                            Welcome
                        </p>
                        <h1
                            className="font-heading text-3xl sm:text-4xl leading-tight tracking-tight max-w-2xl"
                            style={{ color: accent }}
                        >
                            {welcomeMessage}
                        </h1>
                    </div>
                )}

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="space-y-2">
                        <Label htmlFor="client-name">Your name</Label>
                        <Input data-testid="portal-client-name" id="client-name" placeholder="Jane Director" value={clientName} onChange={(e) => setClientName(e.target.value)} />
                    </div>
                    <div className="space-y-2 md:row-span-2">
                        <Label htmlFor="client-notes">Notes (optional)</Label>
                        <Textarea data-testid="portal-client-notes" id="client-notes" rows={4} placeholder="Rough cut of the Nike spot — final color coming Monday." value={clientNotes} onChange={(e) => setClientNotes(e.target.value)} />
                    </div>
                </div>

                {/* Drop zone — accent-aware border + hover */}
                <div
                    data-testid="dropzone-area"
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        if (e.dataTransfer.files?.length) startFiles(e.dataTransfer.files);
                    }}
                    onClick={() => inputRef.current?.click()}
                    className="relative border-2 border-dashed p-16 grid place-items-center cursor-pointer transition-all bg-card"
                    style={{
                        borderColor: dragOver ? accent : undefined,
                        backgroundColor: dragOver ? `${accent}0F` : undefined,
                    }}
                >
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => e.target.files && startFiles(e.target.files)}
                        data-testid="portal-file-input"
                    />
                    <div className="text-center">
                        <UploadCloud
                            className="h-12 w-12 mx-auto"
                            style={{ color: dragOver ? accent : undefined }}
                        />
                        <p className="mt-4 font-heading text-2xl">Drop files here — up to 5 TB each</p>
                        <p className="mt-2 text-sm text-muted-foreground">Or click to browse. Streaming starts immediately.</p>
                    </div>
                </div>

                {/* Permanent security disclaimer */}
                <p
                    className="mt-6 text-sm text-muted-foreground leading-relaxed border-l-2 pl-4"
                    data-testid="portal-security-disclaimer"
                    style={{ borderColor: accent }}
                >
                    <span className="font-medium" style={{ color: accent }}>🔒 Secure High-Speed Transit Pipe:</span> Magnidrop is a dedicated delivery pipeline, not long-term storage. Once your upload completes, your recipient has exactly 7 days to download the assets before the link expires and files are permanently purged from our high-speed network for data privacy and security.
                </p>

                {/* File progress list */}
                {files.length > 0 && (
                    <div className="mt-10 space-y-3" data-testid="portal-file-list">
                        {files.map((f) => {
                            const isActive = ['queued', 'initiating', 'uploading', 'paused'].includes(f.status);
                            return (
                                <div key={f.id} className="border border-border p-4 bg-card" data-testid={`portal-file-row-${f.id}`}>
                                    <div className="flex items-center justify-between text-sm gap-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            {f.status === 'completed' && <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: accent }} />}
                                            {f.status === 'paused' && <PauseCircle className="h-4 w-4 text-amber-500 shrink-0" />}
                                            {['uploading', 'initiating', 'finalizing'].includes(f.status) && <Loader2 className="h-4 w-4 shrink-0 animate-spin" style={{ color: accent }} />}
                                            <p className="font-mono text-xs truncate">{f.name}</p>
                                        </div>
                                        <p className="text-xs text-muted-foreground shrink-0">{(f.size / 1024 ** 3).toFixed(2)} GB · <span className={f.status === 'paused' ? 'text-amber-500' : ''}>{f.status}</span></p>
                                        {isActive && f.ctrl && (
                                            f.status === 'paused' ? (
                                                <button
                                                    data-testid={`portal-upload-resume-${f.id}`}
                                                    onClick={() => f.ctrl.resume()}
                                                    className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border rounded-md hover:text-white transition-colors"
                                                    style={{ borderColor: accent, color: accent }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = accent; e.currentTarget.style.color = '#fff'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; e.currentTarget.style.color = accent; }}
                                                >
                                                    Resume
                                                </button>
                                            ) : (
                                                <button
                                                    data-testid={`portal-upload-pause-${f.id}`}
                                                    onClick={() => f.ctrl.pause()}
                                                    className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border border-border text-muted-foreground rounded-md hover:text-foreground hover:border-foreground transition-colors"
                                                >
                                                    Pause
                                                </button>
                                            )
                                        )}
                                    </div>
                                    <div className="mt-3 h-2 bg-muted overflow-hidden rounded">
                                        <div
                                            className="h-full transition-all"
                                            style={{ width: `${f.pct || 0}%`, backgroundColor: f.status === 'paused' ? '#f59e0b' : accent }}
                                        />
                                    </div>
                                    <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-2">
                                        {(f.pct || 0).toFixed(1)}% {f.completed != null ? `· ${f.completed}/${f.total} chunks` : ''}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="mt-12 flex flex-wrap gap-6 items-center text-xs text-muted-foreground">
                    <span className="flex items-center gap-2"><ShieldCheck className="h-3 w-3" style={{ color: accent }} /> End-to-end presigned URLs</span>
                    <span className="flex items-center gap-2"><Zap className="h-3 w-3" style={{ color: accent }} /> Streaming off disk — no RAM crashes</span>
                </div>
            </main>

            {/* Universal watermark footer — anchored at the absolute bottom of the viewport,
                minimalist + low-contrast, hyperlinked to the Magnidrop home. */}
            <footer
                data-testid="portal-magnidrop-watermark"
                className="mt-auto py-4 text-center border-t border-border/40"
            >
                <Link
                    to="/"
                    className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                >
                    Powered by Magnidrop
                </Link>
            </footer>

            {/* Pipeline-limit reached modal */}
            {limitModal && (
                <PipelineLimitModal
                    details={limitModal}
                    accent={accent}
                    onClose={() => setLimitModal(null)}
                />
            )}
        </div>
    );
}

function PipelineLimitModal({ details, accent, onClose }) {
    const usedGb = ((details?.current_usage_bytes || 0) / 1024 ** 3).toFixed(1);
    const capGb = ((details?.max_allowance_bytes || 0) / 1024 ** 3).toFixed(1);
    const handleUpgrade = () => {
        onClose();
        window.location.href = '/pricing';
    };
    return (
        <div
            data-testid="pipeline-limit-modal"
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[100] grid place-items-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in-0"
        >
            <div
                className="w-full max-w-md bg-[#0E1117] text-neutral-100 shadow-[0_16px_48px_-12px_rgba(0,0,0,0.7)] border"
                style={{ borderColor: `${accent}80` }}
            >
                <div className="p-6 border-b border-neutral-800 flex items-start gap-3">
                    <div
                        className="h-10 w-10 grid place-items-center border shrink-0"
                        style={{ borderColor: `${accent}66`, backgroundColor: `${accent}14` }}
                    >
                        <AlertTriangle className="h-5 w-5" style={{ color: accent }} strokeWidth={2} />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: accent }}>Pipeline paused</p>
                        <h2 className="mt-1 font-heading text-xl font-medium tracking-tight">
                            Monthly transit pool fully utilized
                        </h2>
                    </div>
                </div>
                <div className="p-6 space-y-4">
                    <p className="text-sm text-neutral-300 leading-relaxed">
                        This recipient&apos;s monthly upload allowance is exhausted. Their pipeline is temporarily paused — no data is lost, and any completed chunks stay safe on our end.
                    </p>
                    {details?.max_allowance_bytes ? (
                        <div className="border border-neutral-800 bg-neutral-900/60 p-3 flex justify-between items-center">
                            <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Usage</p>
                            <p className="font-mono text-sm" style={{ color: accent }}>{usedGb} / {capGb} GB</p>
                        </div>
                    ) : null}
                    <p className="text-xs text-neutral-400 leading-relaxed">
                        Ask the recipient to add a top-up or upgrade their plan — uploads resume automatically the moment more capacity is available.
                    </p>
                </div>
                <div className="p-6 border-t border-neutral-800 flex flex-col sm:flex-row-reverse gap-3">
                    <Button
                        data-testid="pipeline-limit-upgrade-btn"
                        onClick={handleUpgrade}
                        className="text-white flex-1 sm:flex-initial"
                        style={{ backgroundColor: accent }}
                    >
                        Upgrade / Add top-up <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <Button
                        data-testid="pipeline-limit-close-btn"
                        onClick={onClose}
                        variant="outline"
                        className="border-neutral-700 text-neutral-300 hover:text-white flex-1 sm:flex-initial"
                    >
                        Close
                    </Button>
                </div>
            </div>
        </div>
    );
}

function ErrorScreen({ message }) {
    return (
        <div className="min-h-screen grid place-items-center px-6">
            <div className="max-w-sm text-center">
                <p className="text-xs uppercase tracking-[0.2em] text-[#FF6B35]">Not found</p>
                <h1 className="mt-2 font-heading text-3xl">{message}</h1>
                <p className="mt-4 text-sm text-muted-foreground">This portal may have been paused or deleted by the recipient.</p>
            </div>
        </div>
    );
}
